import z from 'zod';
import { partySchema } from '../../common';
import { dateSchema } from '../../validation';

/**
 * What kind of event a timeline row represents. Each Lead mutation
 * appends one entry — backed-out edits intentionally do NOT mutate
 * older rows.
 */
export const leadTimelineTypeSchema = z.enum([
  'created',
  'status_changed',
  'assigned',
  'action_logged',
  'note_added',
  'meta_updated',
  'person_added',
  'person_updated',
  'person_removed',
  'converted_link',
  'lost',
  /** A funnel step was completed — see `FunnelService`. */
  'funnel_step',
  /** An offer was turned down, with the reason — see `leadObjectionSchema`. */
  'objection',
]);
export type LeadTimelineType = z.infer<typeof leadTimelineTypeSchema>;

/**
 * A single audit row on `Lead.timeline[]`. Append-only.
 *
 * - `at`     — when the event happened.
 * - `actor`  — who did it (AdminUser party snapshot, or omitted for
 *              system-stamped events such as `created` via the public
 *              endpoint).
 * - `type`   — what kind of event (see {@link leadTimelineTypeSchema}).
 * - `data`   — free-form payload describing the change. Convention:
 *   - `status_changed` → `{ from, to }`
 *   - `assigned`       → `{ from?, to }` (party snapshots)
 *   - `action_logged`  → `{ actionIndex, type, status }`
 *   - `note_added`     → `{ note }`
 *   - `meta_updated`   → `{ changed: string[] }` (top-level keys)
 *   - `person_*`       → `{ index, person }`
 *   - `converted_link` → `{ convertedOrg }`
 *   - `lost`           → `{ reason }`
 *   - `funnel_step`    → `{ product, step }`
 *   - `objection`      → `{ product, ask, note? }`
 */
export const leadTimelineSchema = z.object({
  at: dateSchema,
  actor: partySchema.optional(),
  type: leadTimelineTypeSchema,
  data: z.record(z.unknown()).optional(),
});

export type LeadTimeline = z.infer<typeof leadTimelineSchema>;
